import * as React from 'react';
import { useState } from 'react';
import moment from 'moment';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Checkbox, DatePicker, Form, Input, InputNumber, message, Radio, Select, Spin } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import OWButton from '../ow_button/OWButton';
import { TextButton } from '../text_button/TextButton';
import BackButton from '../back_button/BackButton';
import ClearErrors from '../clear_errors/ClearErrors';
import ProjectTemplateDetailFields from '../project_template_detail_fields/ProjectTemplateDetailFields';
import ProjectDateRangeFields from '../project_date_range_fields/ProjectDateRangeFields';
import ProjectCascadeDueDatesField, {
	getCascadeDueDatesFromValues,
	getCascadeDueDatesInitialValues,
} from '../project_cascade_due_dates_field/ProjectCascadeDueDatesField';
import ProjectInternalTeamsField, {
	confirmLosingProjectAccess,
	getInternalTeamIdsFromValues,
	getInternalTeamsInitialValues,
	willLoseProjectAccess,
} from '../project_internal_teams_field/ProjectInternalTeamsField';
import useMountEffect from '../../hooks/useMountEffect';
import {
	PROJECT_BUDGET_TYPES,
	PROJECT_ENTITY_TYPES,
	PROJECT_ENTITY_TYPE_OPTIONS,
} from '../../constants/ProjectEntities';
import { DELIVERABLE_STATUSES } from '../../constants/DeliverableStatuses';
import { isProjectsV2Enabled } from '../../utils/FeatureFlagUtils';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { debounce } from '../../utils/PerformanceUtils';
import {
	getProjectTemplateDetails,
	getTemplateDetailPrefill,
	getDeliverableDependencyUpdates,
	getTemplateDeliverableDueDates,
	getTemplateItemDueDate,
	getTemplateItemDueDateHint,
} from '../../utils/ProjectTemplateUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { PROJECTS_CRUD_ACTION_CREATORS } from '../../actions/projects_actions';
import {
	projectsRestCrudThunksForBuyer,
	projectsRestCrudThunksForSupplier,
} from '../../thunks/projects_thunks';
import {
	projectTemplatesRestCrudThunksForBuyer,
	projectTemplatesRestCrudThunksForSupplier,
} from '../../thunks/project_template_thunks';
import {
	deliverablesRestCrudThunksForBuyer,
	deliverablesRestCrudThunksForSupplier,
} from '../../thunks/deliverables_thunks';

const PROJECT_FORM_PROJECT_TEMPLATES_AUTOCOMPLETE = 'PROJECT_FORM_PROJECT_TEMPLATES_AUTOCOMPLETE';
const NEW_PROJECT_DELIVERABLES_TC = 'PROJECT_CREATE_NEW_DELIVERABLES';
// Every template deliverable is sent on create, so all of them have to come back to be
// matched up. Explicitly large: the list endpoint's own default is 10.
const NEW_PROJECT_DELIVERABLES_PAGINATION = { current: 1, pageSize: 500 };

const pluralize = (count, singular) => `${count} ${singular}${count === 1 ? '' : 's'}`;

// Turns the picked template's content into the inline children the create call takes.
// The backend does NOT materialize items from projectTemplateId — the frontend sends
// them — so this is what actually creates a project's starting deliverables and tasks.
// orderingId is omitted deliberately: the API defaults it to the array index, which is
// the order shown in the preview.
const getTemplateItemsForCreate = (projectTemplate, entities, startDate, endDate, budgetType) => {
	const deliverableTemplates = nullSafeGetOrElse('deliverableTemplates', projectTemplate, []);
	const taskTemplates = nullSafeGetOrElse('taskTemplates', projectTemplate, []);
	const selectedEntities = entities || [];
	const wantsDeliverables = selectedEntities.includes(PROJECT_ENTITY_TYPES.deliverables);
	const wantsTasks = selectedEntities.includes(PROJECT_ENTITY_TYPES.tasks);
	// A template item's time period is days relative to the project's own calendar, so
	// the due date can only be worked out here, against the dates just entered.
	const itemDueDate = (item) =>
		getTemplateItemDueDate(nullSafeGet('daysForDue', item), startDate, endDate);
	// A deliverable that waits on another counts its days from THAT deliverable's date,
	// not from the project start — resolved across the whole chain in one pass. Tasks keep
	// counting from the project; they have no dependencies of their own.
	const deliverableDueDates = getTemplateDeliverableDueDates(
		deliverableTemplates,
		startDate,
		endDate
	);
	const deliverableDueDate = (deliverable) => {
		const id = nullSafeGet('id', deliverable);
		return id ? deliverableDueDates[id] : itemDueDate(deliverable);
	};
	return {
		...(wantsDeliverables && deliverableTemplates.length
			? {
					deliverables: deliverableTemplates.map((deliverable) => ({
						name: nullSafeGet('name', deliverable),
						description: nullSafeGet('description', deliverable),
						// Only an individual-budget project gives its deliverables budgets of their
						// own. Copying them regardless left per-deliverable amounts on a project
						// created with "No budget" or "Total Budget" — hidden by the deliverable
						// editor, but summed by BudgetVsDeliverablesChart, which also falls back
						// to that sum when the project has no total, so a project explicitly
						// created without a budget would chart as budgeted.
						...(budgetType === PROJECT_BUDGET_TYPES.individual
							? { budget: nullSafeGet('budget', deliverable) }
							: {}),
						dueDate: deliverableDueDate(deliverable),
						// The template's default assignees (assignTo on the template) become the
						// deliverable's assignedTo. Omitted when the template names none, so the
						// API keeps its own default rather than being handed an empty list.
						...(nullSafeGetOrElse('assignTo', deliverable, []).length
							? { assignedTo: deliverable.assignTo }
							: {}),
						// The API stores what it is given and does not default this, so an
						// omitted status persists as NULL and the deliverable shows a blank
						// badge. Every new deliverable starts Not Started — the same value
						// DeliverableFormModal sends when one is created by hand.
						status: DELIVERABLE_STATUSES.notStarted,
						// Nested tasks only when the project declares the tasks entity. Ticking it
						// happens automatically for any template that has them, so this only bites
						// if the user unticks Tasks — in which case honouring that beats risking
						// the API's entity validation on the nested rows.
						...(wantsTasks
							? {
									tasks: nullSafeGetOrElse('tasks', deliverable, []).map((task) => ({
										title: nullSafeGet('title', task),
										dueDate: itemDueDate(task),
										assignedTo: nullSafeGet('assignTo', task),
									})),
							  }
							: {}),
					})),
			  }
			: {}),
		...(wantsTasks && taskTemplates.length
			? {
					tasks: taskTemplates.map((task) => ({
						title: nullSafeGet('title', task),
						dueDate: itemDueDate(task),
						assignedTo: nullSafeGet('assignTo', task),
					})),
			  }
			: {}),
	};
};

// Template content the selected entities exclude. The API rejects children an entity
// doesn't declare, so rather than silently dropping them from the payload we name what
// will be skipped and offer to switch the entity back on.
const getSkippedTemplateItems = (projectTemplate, entities) => {
	const deliverables = nullSafeGetOrElse('deliverableTemplates', projectTemplate, []);
	const tasks = nullSafeGetOrElse('taskTemplates', projectTemplate, []);
	const selectedEntities = entities || [];
	const hasDeliverables = selectedEntities.includes(PROJECT_ENTITY_TYPES.deliverables);
	const hasTasks = selectedEntities.includes(PROJECT_ENTITY_TYPES.tasks);
	const nestedTaskCount = deliverables.reduce(
		(total, deliverable) => total + nullSafeGetOrElse('tasks', deliverable, []).length,
		0
	);
	const skipped = [];
	if (!hasDeliverables && deliverables.length > 0) {
		skipped.push({
			entity: PROJECT_ENTITY_TYPES.deliverables,
			entityLabel: 'Deliverables',
			what: `${pluralize(deliverables.length, 'deliverable')}${
				nestedTaskCount > 0 ? ` and their ${pluralize(nestedTaskCount, 'task')}` : ''
			}`,
		});
	}
	if (!hasTasks && tasks.length > 0) {
		skipped.push({
			entity: PROJECT_ENTITY_TYPES.tasks,
			entityLabel: 'Tasks',
			what: pluralize(tasks.length, 'project task'),
		});
	}
	// Only worth saying separately when the deliverables themselves are coming through —
	// otherwise the line above already accounts for their tasks.
	if (!hasTasks && hasDeliverables && nestedTaskCount > 0) {
		skipped.push({
			entity: PROJECT_ENTITY_TYPES.tasks,
			entityLabel: 'Tasks',
			what: pluralize(nestedTaskCount, 'deliverable task'),
		});
	}
	return skipped;
};

interface ProjectCreateFormProps {
	history: any;
	redirectForwardUrl?: string;
	onSuccess?: any;
	updating: boolean;
	formData: any;
	currentUser: any;
	projectTemplates: any;
	projectTemplatesFetching: boolean;
	createProject: any;
	fetchProjectTemplate: any;
	fetchProjectTemplates: any;
	fetchNewProjectDeliverables: any;
	updateDeliverable: any;
}

const ProjectCreateForm: React.FC<ProjectCreateFormProps> = ({
	history,
	redirectForwardUrl,
	onSuccess,
	updating,
	formData,
	currentUser,
	projectTemplates,
	projectTemplatesFetching,
	createProject,
	fetchProjectTemplate,
	fetchProjectTemplates,
	fetchNewProjectDeliverables,
	updateDeliverable,
}) => {
	const [form] = Form.useForm();
	const [projectTemplate, setProjectTemplate] = useState({});
	const [submitting, setSubmitting] = useState(false);
	// Id of the template currently being read. Submitting while it is in flight would
	// build the payload from the previous (or empty) template: the project would point
	// at the new template id with none of its items, and its required custom fields
	// would not even be validated, since those inputs have not mounted yet.
	const [loadingTemplateId, setLoadingTemplateId] = useState(null);
	// A read that FAILED leaves the same mismatch behind — a selected id with no
	// content — so it keeps the button disabled until the user retries or picks
	// another template, rather than letting a stale/empty template be submitted.
	const [failedTemplateId, setFailedTemplateId] = useState(null);

	const userType = nullSafeGetOrElse('userType', currentUser, 'buyer');
	const projectsV2 = isProjectsV2Enabled();
	const currencyId = nullSafeGetOrElse('id', getCurrency({ currentUser }), 'USD');

	// antd v4 does not re-render the form on every keystroke the way the legacy
	// Form.create HOC did, so anything the layout branches on has to be watched.
	const hasBudget = Form.useWatch('hasBudget', form) || 'yes';
	const budgetType = Form.useWatch('budgetType', form) || PROJECT_BUDGET_TYPES.total;
	const watchedEntities = Form.useWatch('entities', form);
	// The preview names the due date each template item will get, so the effect of the
	// dates above is visible before the project is created rather than after.
	const watchedStartDate = Form.useWatch('startDate', form);
	const watchedEndDate = Form.useWatch('endDate', form);
	const entities = watchedEntities || [PROJECT_ENTITY_TYPES.workOrders];
	const hasDeliverables = entities.includes(PROJECT_ENTITY_TYPES.deliverables);

	useMountEffect(() => {
		fetchProjectTemplates(userType, { limit: 50, offset: 0 });
	});

	const debouncedSearchProjectTemplates = React.useRef(
		debounce(
			(searchText) =>
				fetchProjectTemplates(nullSafeGetOrElse('userType', currentUser, 'buyer'), {
					name: searchText && searchText.trim(),
				}),
			500,
			false
		)
	);

	// A template's items are sent inline on create, and the API rejects children the
	// project's entities don't declare ("Project X does not support deliverables"), so
	// ticking the matching boxes is what makes the items savable at all — not just a
	// convenience. Additive: nothing already selected is removed, and unticking is still
	// allowed, which drops those items from the payload.
	const applyTemplateEntities = (template) => {
		const deliverables = nullSafeGetOrElse('deliverableTemplates', template, []);
		const hasTemplateTasks =
			nullSafeGetOrElse('taskTemplates', template, []).length > 0 ||
			deliverables.some((deliverable) => nullSafeGetOrElse('tasks', deliverable, []).length > 0);
		const current = form.getFieldValue('entities') || [PROJECT_ENTITY_TYPES.workOrders];
		const next = [...current];
		if (deliverables.length > 0 && !next.includes(PROJECT_ENTITY_TYPES.deliverables)) {
			next.push(PROJECT_ENTITY_TYPES.deliverables);
		}
		if (hasTemplateTasks && !next.includes(PROJECT_ENTITY_TYPES.tasks)) {
			next.push(PROJECT_ENTITY_TYPES.tasks);
		}
		if (next.length !== current.length) {
			form.setFieldsValue({ entities: next });
		}
	};

	// Template fields mount only once a template is picked, so any prefill from
	// formData can't ride along in initialValues — it's pushed in once they exist.
	const applyTemplateDetailPrefill = (template) => {
		const prefill = getTemplateDetailPrefill(
			template,
			nullSafeGetOrElse('projectTemplateDetails', formData, [])
		);
		if (Object.keys(prefill).length > 0) {
			form.setFieldsValue(prefill);
		}
	};

	const handleProjectTemplateChange = (projectTemplateId) => {
		if (!projectTemplateId) {
			setLoadingTemplateId(null);
			setFailedTemplateId(null);
			setProjectTemplate({});
			return;
		}
		setLoadingTemplateId(projectTemplateId);
		setFailedTemplateId(null);
		fetchProjectTemplate(userType, projectTemplateId)
			.then((template) => {
				// These reads aren't cancellable, so switching templates quickly can let an
				// earlier response land last. Applying it would pair the newly selected id with
				// the previous template's fields and inline items, so drop anything that is no
				// longer the selection.
				if (`${form.getFieldValue('projectTemplateId')}` !== `${projectTemplateId}`) {
					return;
				}
				setProjectTemplate(template);
				applyTemplateDetailPrefill(template);
				if (isProjectsV2Enabled()) {
					applyTemplateEntities(template);
				}
			})
			.catch(() => {
				if (`${form.getFieldValue('projectTemplateId')}` === `${projectTemplateId}`) {
					setFailedTemplateId(projectTemplateId);
				}
			})
			// Only the newest read clears the flag, so a superseded one resolving late
			// cannot re-enable the button while the current template is still loading.
			.finally(() =>
				setLoadingTemplateId((current) =>
					`${current}` === `${projectTemplateId}` ? null : current
				)
			);
	};

	const retryTemplate = () => handleProjectTemplateChange(form.getFieldValue('projectTemplateId'));

	const enableEntity = (entity) => {
		const current = form.getFieldValue('entities') || [PROJECT_ENTITY_TYPES.workOrders];
		if (!current.includes(entity)) {
			form.setFieldsValue({ entities: [...current, entity] });
		}
	};

	// Dependencies can only be wired once the deliverables exist: InlineDeliverableItem
	// carries no dependency field, so they cannot ride along in the create payload. The
	// created deliverables come back in the order they were sent — the API defaults
	// orderingId to the payload's array index — which is how a template row is matched to
	// the deliverable it produced.
	//
	// The project already exists by this point, so a failure here is reported and
	// swallowed: losing the project over an unset dependency would be the worse outcome.
	const applyTemplateDependencies = (projectId, entities) => {
		const deliverableTemplates = nullSafeGetOrElse('deliverableTemplates', projectTemplate, []);
		const wantsDeliverables = (entities || []).includes(PROJECT_ENTITY_TYPES.deliverables);
		const hasDependencies = deliverableTemplates.some(
			(deliverable) => !!nullSafeGet('dependsOnId', deliverable)
		);
		if (!projectId || !isProjectsV2Enabled() || !wantsDeliverables || !hasDependencies) {
			return Promise.resolve();
		}
		return fetchNewProjectDeliverables(userType, projectId)
			.then((createdDeliverables) => {
				const ordered = [...(createdDeliverables || [])].sort(
					(left, right) =>
						nullSafeGetOrElse('orderingId', left, 0) - nullSafeGetOrElse('orderingId', right, 0)
				);
				// A deliverable update is a full-object PUT — omitting a field clears it — so
				// each one re-sends the record just read with only the dependency added.
				return Promise.all(
					getDeliverableDependencyUpdates(deliverableTemplates, ordered).map((update) =>
						updateDeliverable(userType, {
							...update.deliverable,
							dependsOnDeliverableId: update.dependsOnDeliverableId,
						})
					)
				);
			})
			.catch(() => {
				message.warning(
					'The project was created, but its deliverable dependencies could not be set. Add them on the Deliverables tab.'
				);
			});
	};

	const handleFinish = (values) => {
		const budgeted = values.hasBudget === 'yes';
		const isTotalBudget = budgeted && values.budgetType === PROJECT_BUDGET_TYPES.total;
		const project = {
			title: values.title,
			description: values.additionalInfo,
			// projects-v2 adds start/end dates, budget type, and entities; the legacy form
			// only had a due date + budget.
			...(projectsV2
				? {
						startDate: values.startDate,
						endDate: values.endDate,
						// Keep dueDate populated for backward-compat until the backend fully moves
						// to startDate/endDate.
						dueDate: values.endDate,
						budgetType: budgeted ? values.budgetType : undefined,
						budget: isTotalBudget ? values.budget : undefined,
						entities: values.entities,
						cascadeDependentDueDates: getCascadeDueDatesFromValues(values),
						// "Select All" means every internal tech of the buyer company can see the
						// project, which is the API's own default — so the field is sent only when it
						// actually narrows access (null from the helper = don't narrow).
						supplierFacilityIds: getInternalTeamIdsFromValues(values) || undefined,
						// The picked template's deliverables and tasks, created with the project.
						// Create-only — the API ignores them on update.
						...getTemplateItemsForCreate(
							projectTemplate,
							values.entities,
							values.startDate,
							values.endDate,
							budgeted ? values.budgetType : undefined
						),
				  }
				: {
						dueDate: values.dueDate,
						budget: values.budget,
				  }),
			buyerCompanyId:
				nullSafeGet('buyerCompanyId', formData) ||
				nullSafeGet('facility.buyerCompanyId', currentUser),
			createdBy: nullSafeGet('createdBy', formData) || nullSafeGet('email', currentUser),
			projectTemplateId: values.projectTemplateId
				? parseInt(values.projectTemplateId, 10)
				: undefined,
			projectTemplateDetails: getProjectTemplateDetails(projectTemplate, values),
		};
		const lostAccess = willLoseProjectAccess(currentUser, getInternalTeamIdsFromValues(values));
		const save = () => {
			setSubmitting(true);
			createProject(userType, project)
				.then((record) =>
					// Navigation waits for this, so the project does not open mid-wiring showing
					// only some of its dependencies.
					applyTemplateDependencies(nullSafeGet('id', record), values.entities).then(() => record)
				)
				.then((record) => {
					// Land on the new project's detail page rather than the index. Only fall back to
					// the caller's onSuccess (e.g. ProjectNewPage's history.goBack) when we did NOT
					// redirect forward — otherwise goBack pops the detail page and bounces the user
					// back to the empty create form.
					const newId = nullSafeGet('id', record);
					if (lostAccess) {
						// The creator left their own team out, so the detail page would fail to load.
						history.push(`/${userType}/projects/overview/active`);
					} else if (newId) {
						history.push(`/${userType}/projects/detail/${newId}/details`);
					} else if (redirectForwardUrl) {
						history.push(redirectForwardUrl);
					} else if (onSuccess) {
						onSuccess(record);
					}
				})
				.finally(() => setSubmitting(false));
		};
		if (lostAccess) {
			// Lock the submit button while the dialog is open: OWButton only blocks a repeat
			// click for one tick, so without this a second submit stacks a second dialog.
			setSubmitting(true);
			confirmLosingProjectAccess().then((confirmed) => {
				if (confirmed) {
					save();
				} else {
					setSubmitting(false);
				}
			});
		} else {
			save();
		}
	};

	const projectTemplatesDropdownRecords = getRecordsForTargetCollection(
		projectTemplates,
		PROJECT_FORM_PROJECT_TEMPLATES_AUTOCOMPLETE
	);
	const entityOptions = PROJECT_ENTITY_TYPE_OPTIONS.map((option) =>
		// Every project has work orders — that entity is locked on.
		option.value === PROJECT_ENTITY_TYPES.workOrders ? { ...option, disabled: true } : option
	);

	// Read-only summary of what picking this template will create. Anything the selected
	// entities exclude is struck through rather than quietly missing.
	const renderTemplateItemsPreview = () => {
		const deliverables = nullSafeGetOrElse('deliverableTemplates', projectTemplate, []);
		const tasks = nullSafeGetOrElse('taskTemplates', projectTemplate, []);
		if (deliverables.length === 0 && tasks.length === 0) {
			return null;
		}
		const hasTasksEntity = entities.includes(PROJECT_ENTITY_TYPES.tasks);
		const itemClass = (included) => (included ? 'bodyregular' : 'bodyregularstrikethrough');
		// Rendered only once the project has a date to count from — with neither date set
		// there is no due date to promise, and saying "14 days" alone would imply one.
		const dueDateHint = (item) => {
			const hint = getTemplateItemDueDateHint(
				nullSafeGet('daysForDue', item),
				watchedStartDate,
				watchedEndDate
			);
			return hint ? (
				<span className="footnoteregular ml-2 text-character-secondary">{hint}</span>
			) : null;
		};

		// A dependent deliverable's date comes off the one it waits on, so the plain
		// days-from-start hint would name a date the project will not actually get. The
		// preview has to agree with what create sends.
		const previewDueDates = getTemplateDeliverableDueDates(
			deliverables,
			watchedStartDate,
			watchedEndDate
		);
		const deliverableDueDateHint = (deliverable) => {
			const id = nullSafeGet('id', deliverable);
			const dependsOn = id
				? deliverables.find(
						(candidate) => nullSafeGet('id', candidate) === nullSafeGet('dependsOnId', deliverable)
				  )
				: null;
			if (!dependsOn) {
				return dueDateHint(deliverable);
			}
			const dueDate = previewDueDates[id];
			const days = nullSafeGet('daysForDue', deliverable);
			return dueDate ? (
				<span className="footnoteregular ml-2 text-character-secondary">
					{`${days} ${Number(days) === 1 ? 'day' : 'days'} after ${nullSafeGetOrElse(
						'name',
						dependsOn,
						'the deliverable it depends on'
					)} · ${dueDate.format('MMM D, YYYY')}`}
				</span>
			) : null;
		};
		return (
			<div className="mb-6 border border-solid border-owgray p-4" style={{ maxWidth: 640 }}>
				<div className="bodymedium mb-2">This template will create</div>
				{deliverables.length > 0 ? (
					<>
						<div className="footnoteMedium text-character-secondary">Deliverables</div>
						<ul className="mb-0">
							{deliverables.map((deliverable, index) => {
								const taskCount = nullSafeGetOrElse('tasks', deliverable, []).length;
								return (
									<li key={index} className={itemClass(hasDeliverables)}>
										{nullSafeGet('name', deliverable)}
										{taskCount > 0 ? (
											<span className={itemClass(hasDeliverables && hasTasksEntity)}>
												{` — ${pluralize(taskCount, 'task')}`}
											</span>
										) : null}
										{deliverableDueDateHint(deliverable)}
									</li>
								);
							})}
						</ul>
					</>
				) : null}
				{tasks.length > 0 ? (
					<>
						<div className="footnoteMedium mt-2 text-character-secondary">Project tasks</div>
						<ul className="mb-0">
							{tasks.map((task, index) => (
								<li key={index} className={itemClass(hasTasksEntity)}>
									{nullSafeGet('title', task)}
									{dueDateHint(task)}
								</li>
							))}
						</ul>
					</>
				) : null}
			</div>
		);
	};

	const renderSkippedTemplateItems = () => {
		const skipped = getSkippedTemplateItems(projectTemplate, entities);
		if (skipped.length === 0) {
			return null;
		}
		return (
			<div className="mt-2 flex flex-col gap-1" style={{ maxWidth: 640 }}>
				{skipped.map((item, index) => (
					<div key={index} className="footnoteregular flex items-start gap-2 text-sunsetOrange-8">
						{/* Size the icon with the 12px text (the repo idiom for icons in a text run).
						    line-height needs the ! because antd's .anticon sets line-height:0 in a
						    single-class rule, so without it the winner depends on stylesheet order. */}
						<ExclamationCircleOutlined className="text-[12px] !leading-[20px]" />
						<span>
							{`${item.what} from this template won't be created while ${item.entityLabel} is off. `}
							<TextButton onClick={() => enableEntity(item.entity)}>
								{`Enable ${item.entityLabel}`}
							</TextButton>
						</span>
					</div>
				))}
			</div>
		);
	};

	return (
		<Form
			form={form}
			layout="vertical"
			onFinish={handleFinish}
			className="supplierForm"
			initialValues={{
				title: nullSafeGet('title', formData),
				additionalInfo: nullSafeGet('description', formData),
				// Legacy path kept its pre-filled default + time-of-day picker.
				dueDate: nullSafeGet('dueDate', formData) ? moment(formData.dueDate) : moment(),
				hasBudget: 'yes',
				budgetType: PROJECT_BUDGET_TYPES.total,
				budget: nullSafeGet('budget', formData),
				projectTemplateId: nullSafeGet('projectTemplateId', formData),
				entities: nullSafeGetOrElse('entities', formData, [PROJECT_ENTITY_TYPES.workOrders]),
				...getInternalTeamsInitialValues(formData),
				...getCascadeDueDatesInitialValues(formData),
			}}
		>
			<ClearErrors actionCreators={PROJECTS_CRUD_ACTION_CREATORS} />
			<Form.Item
				label="Title"
				name="title"
				rules={[{ required: true, message: 'This field is required.' }]}
			>
				<Input style={{ maxWidth: 640 }} />
			</Form.Item>
			<Form.Item label="Scope" name="additionalInfo">
				<Input.TextArea
					style={{ maxWidth: 640 }}
					autoSize={{ minRows: 3 }}
					placeholder="Describe the project's scope — what's included, goals, or any context"
				/>
			</Form.Item>
			{projectsV2 ? (
				<ProjectDateRangeFields />
			) : (
				<Form.Item
					label="Due date"
					name="dueDate"
					rules={[{ required: true, message: 'This field is required.' }]}
				>
					<DatePicker
						style={{ width: '100%', maxWidth: 640 }}
						format="MMM D, YYYY h:mm A"
						showTime
					/>
				</Form.Item>
			)}
			{projectsV2 ? (
				<>
					<Form.Item label="Does this project have a budget?" name="hasBudget">
						<Radio.Group>
							<Radio value="yes">Yes</Radio>
							<Radio value="no">No</Radio>
						</Radio.Group>
					</Form.Item>
					{hasBudget === 'yes' ? (
						<Form.Item name="budgetType">
							<Radio.Group>
								<div className="flex flex-col gap-3">
									<div>
										<Radio value={PROJECT_BUDGET_TYPES.total}>Total Budget</Radio>
										<div
											className="footnoteregular text-character-secondary"
											style={{ marginLeft: 24 }}
										>
											Budget allocated for the entire project.
										</div>
									</div>
									<div>
										<Radio value={PROJECT_BUDGET_TYPES.individual} disabled={!hasDeliverables}>
											Individual Budget
										</Radio>
										<div
											className="footnoteregular text-character-secondary"
											style={{ marginLeft: 24 }}
										>
											Each deliverable created will have its own budget
											{hasDeliverables ? '' : ' — enable Deliverables to use this'}
										</div>
									</div>
								</div>
							</Radio.Group>
						</Form.Item>
					) : null}
					{hasBudget === 'yes' && budgetType === PROJECT_BUDGET_TYPES.total ? (
						<Form.Item
							label="Budget"
							name="budget"
							rules={[{ required: true, message: 'This field is required.' }]}
						>
							<InputNumber
								min={0}
								style={{ display: 'flex', width: '100%', maxWidth: 640 }}
								addonAfter={currencyId}
							/>
						</Form.Item>
					) : null}
				</>
			) : (
				<Form.Item
					label="Budget"
					name="budget"
					rules={[{ required: true, message: 'This field is required.' }]}
				>
					<InputNumber
						min={0}
						style={{ display: 'flex', width: '100%', maxWidth: 640 }}
						addonAfter={currencyId}
					/>
				</Form.Item>
			)}
			<Form.Item label="Project Template" name="projectTemplateId">
				<Select
					notFoundContent={projectTemplatesFetching ? <Spin size="small" /> : null}
					placeholder="Type to search for project template"
					filterOption={false}
					showSearch={true}
					allowClear
					onSearch={(searchText) => debouncedSearchProjectTemplates.current(searchText)}
					onChange={handleProjectTemplateChange}
					style={{ maxWidth: 640 }}
				>
					{projectTemplatesDropdownRecords.map((template) => (
						<Select.Option value={template.id} key={template.id}>
							{template.name}
						</Select.Option>
					))}
				</Select>
			</Form.Item>
			{failedTemplateId ? (
				<div className="footnoteregular mb-4 flex items-start gap-2" style={{ maxWidth: 640 }}>
					<ExclamationCircleOutlined className="text-[12px] !leading-[20px] text-sunsetOrange-8" />
					<span className="text-sunsetOrange-8">
						{
							"Couldn't load this template, so its deliverables, tasks and fields aren't available. "
						}
						<TextButton onClick={retryTemplate}>Retry</TextButton>
					</span>
				</div>
			) : null}
			{projectsV2 ? renderTemplateItemsPreview() : null}
			<ProjectTemplateDetailFields projectTemplate={projectTemplate} />
			{projectsV2 ? <ProjectInternalTeamsField form={form} /> : null}
			{projectsV2 ? (
				<Form.Item label="Project Entities" required className="!mb-4">
					<Form.Item
						name="entities"
						noStyle
						rules={[{ required: true, message: 'Select at least one entity.' }]}
					>
						<Checkbox.Group
							options={entityOptions}
							onChange={(checked) => {
								// Individual budget requires Deliverables — if it's removed, snap the budget
								// type back to Total so we never save individual-with-no-deliverables.
								if (
									!checked.includes(PROJECT_ENTITY_TYPES.deliverables) &&
									form.getFieldValue('budgetType') === PROJECT_BUDGET_TYPES.individual
								) {
									form.setFieldsValue({ budgetType: PROJECT_BUDGET_TYPES.total });
								}
							}}
						/>
					</Form.Item>
					{renderSkippedTemplateItems()}
				</Form.Item>
			) : null}
			{projectsV2 ? <ProjectCascadeDueDatesField hasDeliverables={hasDeliverables} /> : null}
			<Form.Item>
				<OWButton
					type="primary"
					htmlType="submit"
					// Also disabled while a template read is in flight — see loadingTemplateId.
					loading={updating || submitting || !!loadingTemplateId}
					// A failed template read leaves a selected id with no content, so block the
					// save until it is retried or the selection changes.
					disabled={!!failedTemplateId}
					className="contactForm__button"
				>
					Create project
				</OWButton>
				<span style={{ marginLeft: '16px' }}>
					<BackButton buttonText="Cancel" />
				</span>
			</Form.Item>
		</Form>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	redirectForwardUrl: ownProps.redirectForwardUrl,
	onSuccess: ownProps.onSuccess,
	formData: ownProps.formData,
	projectTemplatesFetching: state.project_templates.fetching,
	projectTemplates: state.project_templates,
	updating: state.work_orders.updating,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
	createProject: (userType, project) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectsRestCrudThunksForSupplier.create(project)
				: projectsRestCrudThunksForBuyer.create(project)
		),
	fetchProjectTemplate: (userType, id) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectTemplatesRestCrudThunksForSupplier.readOne(id)
				: projectTemplatesRestCrudThunksForBuyer.readOne(id)
		),
	fetchNewProjectDeliverables: (userType, projectId) =>
		dispatch(
			(userType === ROLE_TYPES.SUPPLIER
				? deliverablesRestCrudThunksForSupplier
				: deliverablesRestCrudThunksForBuyer
			).read({ projectId }, NEW_PROJECT_DELIVERABLES_TC, NEW_PROJECT_DELIVERABLES_PAGINATION)
		),
	updateDeliverable: (userType, deliverable) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? deliverablesRestCrudThunksForSupplier.update(deliverable)
				: deliverablesRestCrudThunksForBuyer.update(deliverable)
		),
	fetchProjectTemplates: (userType, params) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectTemplatesRestCrudThunksForSupplier.read(
						params,
						PROJECT_FORM_PROJECT_TEMPLATES_AUTOCOMPLETE
				  )
				: projectTemplatesRestCrudThunksForBuyer.read(
						params,
						PROJECT_FORM_PROJECT_TEMPLATES_AUTOCOMPLETE
				  )
		),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ProjectCreateForm as any));
